/**
 * Hooks LeadBeton Sprint 6 (Modules 13 AffectationOf · 15 Dashboard DG).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  operationsApi,
  type AffectationOfListParams,
  type CreateAffectationOfPayload,
  type UpdateAffectationOfPayload,
} from '@/api/operations';

const DASHBOARD_DG_KEY = ['operations', 'beton', 'dashboard-dg']     as const;
const AFFECTATIONS_KEY = ['operations', 'beton', 'affectations-of']  as const;

export const useDashboardDg = () =>
  useQuery({
    queryKey:        DASHBOARD_DG_KEY,
    queryFn:         () => operationsApi.beton.getDashboardDg(),
    staleTime:       60_000,
    refetchInterval: 120_000,
  });

export const useAffectationsOf = (params: AffectationOfListParams = {}) =>
  useQuery({
    queryKey:  [...AFFECTATIONS_KEY, params],
    queryFn:   () => operationsApi.beton.listAffectationsOf(params),
    staleTime: 30_000,
  });

export const useAffectationsByOrdre = (ordreId: string | undefined) =>
  useQuery({
    queryKey:  [...AFFECTATIONS_KEY, 'by-ordre', ordreId],
    queryFn:   () => operationsApi.beton.getAffectationsByOrdre(ordreId!),
    enabled:   !!ordreId,
    staleTime: 30_000,
  });

const invalidateAffectations = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: AFFECTATIONS_KEY });
  qc.invalidateQueries({ queryKey: DASHBOARD_DG_KEY });
};

export const useCreateAffectationOf = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAffectationOfPayload) => operationsApi.beton.createAffectationOf(payload),
    onSuccess:  () => invalidateAffectations(qc),
  });
};

export const useUpdateAffectationOf = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateAffectationOfPayload }) =>
      operationsApi.beton.updateAffectationOf(id, payload),
    onSuccess: () => invalidateAffectations(qc),
  });
};

export const useDeleteAffectationOf = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteAffectationOf(id),
    onSuccess:  () => invalidateAffectations(qc),
  });
};
